import React from "react";
import { SnackbarKey, useSnackbar } from "notistack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const ENQUEUE_DEDUPE_MS = 300;
let lastGlobalEnqueue = { fingerprint: "", at: 0 };

export type SnackbarStatus = "ok" | "ng" | string;

export type SnackbarData = {
  status?: SnackbarStatus;
  message?: string;
};

export type SnackbarCustomProps = {
  data?: SnackbarData;
  openStatus: boolean;
  onClose: () => void;
};

const SnackbarCustom = ({
  data,
  openStatus,
  onClose,
}: SnackbarCustomProps): null => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const onCloseRef = React.useRef(onClose);
  const prevOpenRef = React.useRef(false);
  const prevFingerprintRef = React.useRef<string | null>(null);

  const message = data?.message;
  const severity = data?.status === "ok" ? "success" : "error";

  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  React.useEffect(() => {
    if (!openStatus) {
      prevOpenRef.current = false;
      prevFingerprintRef.current = null;
      return undefined;
    }

    if (!message) {
      return undefined;
    }

    const fingerprint = `${severity}:${message}`;
    const risingEdge = !prevOpenRef.current;
    const fingerprintChanged = prevFingerprintRef.current !== fingerprint;

    prevOpenRef.current = true;

    if (!risingEdge && !fingerprintChanged) {
      return undefined;
    }

    const now = Date.now();
    if (
      lastGlobalEnqueue.fingerprint === fingerprint &&
      now - lastGlobalEnqueue.at < ENQUEUE_DEDUPE_MS
    ) {
      return undefined;
    }

    lastGlobalEnqueue = { fingerprint, at: now };
    prevFingerprintRef.current = fingerprint;

    const handleDismiss = () => {
      onCloseRef.current();
    };

    const action = (key: SnackbarKey) => (
      <IconButton
        name="closeButtonSnackbar"
        aria-label="close"
        color="inherit"
        onClick={() => {
          closeSnackbar(key);
          handleDismiss();
        }}
        style={{
          padding: "0",
          margin: "0",
        }}
      >
        <CloseIcon />
      </IconButton>
    );

    enqueueSnackbar(message, {
      key: fingerprint,
      preventDuplicate: true,
      autoHideDuration: 3000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
      variant: severity,
      action,
      onClose: (_event, reason) => {
        if (reason === "timeout") {
          handleDismiss();
        }
      },
    });

    return undefined;
  }, [openStatus, message, severity, enqueueSnackbar, closeSnackbar]);

  return null;
};

export default SnackbarCustom;
